import { useState } from 'react';

interface PixelLogoProps {
  imageUrl?: string;
  pixelImageUrl?: string;
  shopName: string;
  size?: number;
  glowColor?: string; // kept for prop-surface stability — coral tiles no longer glow
  className?: string;
}

// Stable per-shop accent from the retro palette — the same shop always gets
// the same color everywhere its mark appears. Exported for the storefront
// accent-color swatch picker (settings), which offers these as presets.
export const MARK_PALETTE = ['#00ffff', '#ff00ea', '#ffff00', '#3dba4c', '#ff9f1c', '#a78bfa'];

export function shopMarkColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return MARK_PALETTE[h % MARK_PALETTE.length];
}

/**
 * Displays a shop logo with pixel-art rendering, in the coral tile language
 * (gold initial tile / white image tile, ink border — mockup 10c).
 * Falls back to a generated initial letter if no image.
 */
export default function PixelLogo({
  imageUrl,
  pixelImageUrl,
  shopName,
  size = 64,
  className = '',
}: PixelLogoProps) {
  const [imgError, setImgError] = useState(false);

  const src = pixelImageUrl || imageUrl;

  if (!src || imgError) {
    const initial = shopName?.charAt(0)?.toUpperCase() || '?';
    return (
      <div
        className={`flex items-center justify-center rounded-[10px] border-[3px] border-primary bg-accent-alt ${className}`}
        style={{ width: size, height: size }}
      >
        <span className="font-heading text-primary" style={{ fontSize: size * 0.35 }}>
          {initial}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-[10px] border-[3px] border-primary bg-white overflow-hidden flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <img
        src={src}
        alt={`${shopName} logo`}
        onError={() => setImgError(true)}
        className="w-full h-full object-contain"
        style={{ imageRendering: 'pixelated' }}
        loading="lazy"
      />
    </div>
  );
}
